import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { GuardianInstallation } from '../../api/types';
import { readInstallVersions } from '../../api/releases';
import type { ListState } from '../common/AsyncList';

/**
 * Lifecycle of one product in one customer tenant, as Guardian will record
 * it (docs/ARCHITECTURE-END-TO-END.md, "Installation states").
 */
export type InstallationState =
  | 'ReadyToDeploy'
  | 'Installing'
  | 'Installed'
  | 'AwaitingCustomerAzure'
  | 'Live'
  | 'InUse'
  | 'Failed'
  | 'Suspended'
  | 'Removed';

/** One installation record from FirstIQ Guardian. */
export interface Installation {
  tenantId: string;
  customerName: string;
  productId: string;
  productName: string;
  version: string;
  siteUrl: string;
  state: InstallationState;
  installedBy?: string;
  updatedAt: string;
  /** Deployment session id, when Guardian recorded one. */
  deploymentId?: string;
  /** Release-management fields from Guardian; null when Guardian does not send them. */
  latestVersion: string | null;
  outdated: boolean | null;
  runningWithdrawn: boolean;
}

/** Installation records for every customer tenant, from FirstIQ Guardian via GET /api/installations. */
export function useInstallations(): ListState<Installation> {
  const q = useQuery({ queryKey: ['installations'], queryFn: () => api<GuardianInstallation[]>('installations') });
  return {
    loading: q.isLoading,
    error: q.error ? (q.error as Error).message : null,
    items: (q.data ?? []).map((i) => {
      const v = readInstallVersions(i);
      return {
        tenantId: i.tenantId,
        customerName: i.CustomerName || i.tenantId,
        productId: i.appId,
        productName: i.appId,
        version: v.runningVersion || '',
        siteUrl: i.SiteUrl || '',
        state: i.State as InstallationState,
        installedBy: i.UpdatedBy || undefined,
        updatedAt: i.StateChangedAt,
        deploymentId: i.DeploymentId || undefined,
        latestVersion: v.latestVersion,
        outdated: v.outdated,
        runningWithdrawn: v.runningWithdrawn,
      };
    }),
  };
}
